import {createContext, useContext} from "react";
import {formatMoney} from "../../format";
import {publicRegistrationLabelKeys} from "../../i18n/publicPages";
import {usePublicPageLabels} from "../../i18n/publicPageLabels";
import type {
    PublicParticipationFeeOption,
    PublicRegistrationLabels,
    PublicRegistrationLanguage,
    PublicRegistrationOption,
    PublicRegistrationOptionChoice
} from "./types";

export type RegistrationCopy = {
    labels: PublicRegistrationLabels;
    language: PublicRegistrationLanguage;
    formatAmount: (amount: number) => string;
    participationFeeName: (fee: PublicParticipationFeeOption) => string;
    participationFeeLabel: (fee: PublicParticipationFeeOption) => string;
    registrationOptionName: (option: PublicRegistrationOption) => string;
    registrationOptionLabel: (option: PublicRegistrationOption) => string;
    registrationOptionChoiceLabel: (choice: PublicRegistrationOptionChoice) => string;
};

export const usePublicRegistrationLabels = (language: PublicRegistrationLanguage): PublicRegistrationLabels =>
    usePublicPageLabels("public-registration", publicRegistrationLabelKeys, language);

// Labels plus the bilingual fee/activity/money formatting every registration section shares.
export const buildRegistrationCopy = (
    labels: PublicRegistrationLabels,
    language: PublicRegistrationLanguage
): RegistrationCopy => {
    const localized = (hr: string | null | undefined, en: string | null | undefined) =>
        (language === "en" ? en : hr) || hr || en;
    const formatAmount = (amount: number) => formatMoney(amount, "EUR", language);
    const participationFeeName = (fee: PublicParticipationFeeOption) => {
        const label = localized(fee.labelHr, fee.labelEn) || labels.participationFee;
        const period = fee.pricePeriodName?.trim();
        return period ? `${label} (${period})` : label;
    };
    const registrationOptionName = (option: PublicRegistrationOption) =>
        localized(option.labelHr, option.labelEn) || labels.additionalOptions;

    return {
        labels,
        language,
        formatAmount,
        participationFeeName,
        participationFeeLabel: fee => `${participationFeeName(fee)} - ${formatAmount(Number(fee.amount ?? 0))}`,
        registrationOptionName,
        registrationOptionLabel: option => {
            const price = Number(option.price ?? 0);
            const label = registrationOptionName(option);
            return price > 0 ? `${label} - ${formatAmount(price)}` : label;
        },
        registrationOptionChoiceLabel: choice => localized(choice.labelHr, choice.labelEn) || choice.value
    };
};

const RegistrationCopyContext = createContext<RegistrationCopy | null>(null);

export const RegistrationCopyProvider = RegistrationCopyContext.Provider;

export const useRegistrationCopy = (): RegistrationCopy => {
    const copy = useContext(RegistrationCopyContext);
    if (!copy) {
        throw new Error("useRegistrationCopy must be used inside RegistrationCopyProvider.");
    }

    return copy;
};
